import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Shield, RefreshCw } from "lucide-react";
import axios from "axios";
import { useUser } from "@/contexts/UserContext";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VerifyOTPPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserData } = useUser();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [countdown, setCountdown] = useState(60);
  const inputRefs = useRef([]);
  
  // Get email from location state
  const email = location.state?.email;
  const otpHint = location.state?.otpHint; // For testing only

  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only take last digit
    setOtp(newOtp);

    // Move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all filled
    if (index === 5 && value) {
      const fullOtp = newOtp.join("");
      if (fullOtp.length === 6) {
        handleVerify(fullOtp);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      if (pastedData.length === 6) {
        handleVerify(pastedData);
      }
    }
  };

  const handleVerify = async (otpCode) => {
    const code = otpCode || otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter complete 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-otp`, {
        email: email,
        otp: code
      }, { withCredentials: true });

      if (response.data.success) {
        // Set user data in context
        setUserData(response.data.user, response.data.session_token);
        toast.success("Login successful!");
        navigate("/dashboard");
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Verification failed";
      toast.error(detail);
      // Clear OTP on error
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;

    setResendLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/resend-otp`, {
        email: email
      });

      if (response.data.success) {
        toast.success("New OTP sent!");
        setCountdown(60);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        
        // Show OTP hint for testing
        if (response.data.otp_hint) {
          toast.info(`Test OTP: ${response.data.otp_hint}`, { duration: 10000 });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend OTP");
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md p-8 relative z-10 shadow-2xl" data-testid="otp-card">
        {/* Back Button */}
        <button
          onClick={() => navigate("/login")}
          className="absolute top-4 left-4 text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm"
          data-testid="otp-back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-violet-600" />
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Verify Your Identity</h1>
          <p className="text-gray-500 text-sm mt-2">
            Enter the 6-digit code sent to
          </p>
          <p className="text-violet-600 font-medium">{email}</p>
        </div>

        {/* Test OTP Hint */}
        {otpHint && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 text-center">
            <p className="text-sm text-yellow-800">
              Test OTP: <span className="font-mono font-bold">{otpHint}</span>
            </p>
          </div>
        )}

        {/* OTP Input */}
        <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
          {otp.map((digit, index) => (
            <Input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-violet-500"
              disabled={loading}
              data-testid={`otp-input-${index}`}
            />
          ))}
        </div>

        {/* Verify Button */}
        <Button
          onClick={() => handleVerify()}
          className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 h-12"
          disabled={loading || otp.join("").length !== 6}
          data-testid="verify-otp-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            "Verify & Login"
          )}
        </Button>

        {/* Resend */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500 mb-2">Didn't receive the code?</p>
          <Button
            variant="ghost"
            onClick={handleResend}
            disabled={countdown > 0 || resendLoading}
            className="text-violet-600"
            data-testid="resend-otp-btn"
          >
            {resendLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {countdown > 0 ? `Resend in ${countdown}s` : "Resend OTP"}
          </Button>
        </div>

        {/* Security Note */}
        <p className="text-xs text-gray-400 text-center mt-6">
          OTP expires in 10 minutes. Keep this code private.
        </p>
      </Card>
    </div>
  );
}
